import React from "react";
import { FiClock, FiBriefcase, FiTarget, FiCheck } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;

  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return `${day} ${month} ${year}, ${time}`;
};

export const isTaskCompleted = (task) => {
  if (!task) return false;
  return Boolean(
    task.status === "Complete" ||
    task.status === "Completed" ||
    task.completed_at ||
    task.completion_date
  );
};

export const isTaskOverdue = (task) => {
  if (!task || !task.due_date) return false;
  if (isTaskCompleted(task)) return false;
  return task.status === "Overdue" || new Date(task.due_date) < new Date();
};

export const isCompletedTaskOverdue = (task) => {
  if (!task) return false;
  if (!isTaskCompleted(task)) return false;

  if (task.completion_type === "overdue") return true;
  if (task.points !== undefined && task.points !== null && task.points < 0) return true;

  const completionDate = task.completed_at || task.completion_date;
  if (completionDate && task.due_date) {
    const comp = new Date(completionDate);
    const due = new Date(task.due_date);
    if (!isNaN(comp.getTime()) && !isNaN(due.getTime())) {
      return comp > due;
    }
  }
  return false;
};

export const taskColumns = ({
  selectedIds = [],
  handleSelect,
  handleSelectAll,
  totalRows = 0,
  onCompleteTask,
}) => [
  {
    id: "select",
    label: (
      <input
        type="checkbox"
        checked={totalRows > 0 && selectedIds.length === totalRows}
        onChange={handleSelectAll}
      />
    ),
    render: (row) => (
      <input
        type="checkbox"
        checked={selectedIds.includes(String(row.id))}
        onChange={() => handleSelect(row.id)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  },
  {
    id: "title",
    label: "Task",
    render: (row) => (
      <div className="task-title-cell">
        <div className="task-main-title" title={row.title}>
          {row.title}
        </div>
        {row.description && (
          <div className="task-sub-description" title={row.description}>
            {row.description}
          </div>
        )}
      </div>
    ),
    exportValue: (row) => row.title,
  },
  {
    id: "due_date",
    label: "Due Date",
    render: (row) => {
      if (!row.due_date) {
        return <span style={{ color: "#94a3b8", fontSize: "13px" }}>-</span>;
      }
      const overdue = isTaskOverdue(row);
      return (
        <div className={`task-due-date ${overdue ? "is-overdue" : ""}`}>
          <div className="task-due-date-value">
            <FiClock size={13} className="task-due-clock-icon" />
            <span>{formatDateTime(row.due_date)}</span>
          </div>
        </div>
      );
    },
    exportValue: (row) => formatDateTime(row.due_date),
  },
  {
    id: "related_to",
    label: "Related To",
    render: (row) => {
      if (row.deal_title) {
        return (
          <span className="task-entity-chip" title={`Deal: ${row.deal_title}`}>
            <FiBriefcase size={12} color="#16a34a" className="task-entity-icon" />
            <span className="task-entity-text">{row.deal_title}</span>
          </span>
        );
      }
      if (row.lead_title) {
        return (
          <span className="task-entity-chip" title={`Lead: ${row.lead_title}`}>
            <FiTarget size={12} color="#0284c7" className="task-entity-icon" />
            <span className="task-entity-text">{row.lead_title}</span>
          </span>
        );
      }
      if (row.organization_name) {
        return (
          <span className="task-entity-chip" title={`Org: ${row.organization_name}`}>
            <LuBuilding2 size={12} color="#9333ea" className="task-entity-icon" />
            <span className="task-entity-text">{row.organization_name}</span>
          </span>
        );
      }
      return <span style={{ color: "#94a3b8", fontSize: "13px" }}>-</span>;
    },
    exportValue: (row) =>
      row.deal_title || row.lead_title || row.organization_name || "",
  },
  {
    id: "assigned_user_name",
    label: "Assigned To",
    render: (row) => (
      <span
        className="task-assigned-user"
        title={row.assigned_user_name || "Unassigned"}
      >
        {row.assigned_user_name || "Unassigned"}
      </span>
    ),
    exportValue: (row) => row.assigned_user_name || "Unassigned",
  },
  // ==========================================================
  // COIN & POINT SYSTEM: Task Points Table Column
  // Visual states:
  // 1. Completed On Time: green pill with +5 pts
  // 2. Completed Overdue: red pill with -5 pts penalty
  // 3. Pending Overdue: red warning pill indicating -5 pts will be deducted
  // 4. Pending On Time: neutral dash indicating pending outcome
  // ==========================================================
  {
    id: "points",
    label: "Points",
    render: (row) => {
      const isCompleted = isTaskCompleted(row);
      if (isCompleted) {
        const pts = row.points !== undefined && row.points !== null ? row.points : 5;
        if (pts >= 0) {
          return (
            <span
              className="task-points-pill earned"
              title={row.completion_type ? `Completed ${row.completion_type} (+${pts} pts)` : `+${pts} Points`}
            >
              +{pts} pts
            </span>
          );
        } else {
          return (
            <span
              className="task-points-pill penalty"
              title={`Completed Overdue (${pts} pts)`}
            >
              {pts} pts
            </span>
          );
        }
      }

      const overdue = row.status === "Overdue" || isTaskOverdue(row);
      if (overdue) {
        return (
          <span
            className="task-points-pill penalty-pending"
            title="Overdue task (-5 pts penalty upon completion)"
          >
            -5 pts
          </span>
        );
      }

      return (
        <span className="task-points-pending" title="Pending completion (+5 pts on time)">
          —
        </span>
      );
    },
    exportValue: (row) => {
      if (isTaskCompleted(row)) {
        return row.points !== undefined && row.points !== null ? `${row.points}` : "5";
      }
      return "-";
    },
  },
  {
    id: "status",
    label: "Status",
    render: (row) => {
      const isCompleted = isTaskCompleted(row);
      const isCompletedOverdue = isCompletedTaskOverdue(row);
      const overdue = !isCompleted && isTaskOverdue(row);

      let statusText = "Pending";
      let pillClass = "status-pill-pending";

      if (isCompleted) {
        statusText = "Completed";
        pillClass = isCompletedOverdue
          ? "status-pill-completed-overdue"
          : "status-pill-completed";
      } else if (overdue) {
        statusText = "Overdue";
        pillClass = "status-pill-overdue";
      } else if (row.status === "In Progress") {
        statusText = "In Progress";
        pillClass = "status-pill-pending";
      } else if (row.status) {
        statusText = row.status;
      }

      return (
        <div className="task-status-cell-wrapper" onClick={(e) => e.stopPropagation()}>
          <span className={`task-status-pill ${pillClass}`}>
            <span className="status-pill-dot" />
            {statusText}
          </span>
        </div>
      );
    },
    exportValue: (row) => {
      if (isTaskCompleted(row)) return "Completed";
      if (isTaskOverdue(row)) return "Overdue";
      return row.status || "Pending";
    },
  },
  {
    id: "actions",
    label: "Actions",
    render: (row) => {
      const isCompleted = isTaskCompleted(row);
      if (!isCompleted && onCompleteTask) {
        return (
          <div className="task-actions-cell" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn-quick-complete-action"
              title="Complete Task & Update Follow-up"
              onClick={() => onCompleteTask(row)}
            >
              <FiCheck size={12} /> Complete
            </button>
          </div>
        );
      }
      return <span style={{ color: "#94a3b8", fontSize: "13px" }}>-</span>;
    },
    exportValue: () => "",
  },
];
